import React, { useCallback, useState } from 'react';
import { toast } from 'react-toastify';

import Button, { BUTTON_SIZES, BUTTON_TYPES } from '@buyside/components/presentation/Button/button';
import DropzoneInput from '@buyside/components/presentation/Dropzone/DropzoneInput';
import Modal from '@buyside/components/presentation/Modal/modal';
import TickerPostUpload from '@buyside/components/presentation/TickerUploader/tickerPostUpload';
import TickerPreUpload from '@buyside/components/presentation/TickerUploader/tickerPreUpload';

import {
  useMassFollowEntitiesMutation,
  useMassTickerSearchLazyQuery
} from '@interfaces/graphql-typings';

import styles from '@buyside/components/presentation/TickerUploader/tickerUploadStyles.scss';

const accept = {
  'text/csv': ['.csv']
};

const TickerUploader: React.FC = (): React.ReactElement => {
  const [fileName, setFileName] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [page, setPage] = useState(0);

  const [lookupTickers, { loading: lookupTickersLoading, data }] = useMassTickerSearchLazyQuery({
    notifyOnNetworkStatusChange: true
  });

  const [
    massFollowEntities,
    { loading: massFollowEntitiesLoading }
  ] = useMassFollowEntitiesMutation({
    onCompleted: () => {
      setIsModalOpen(false);
    },
    onError: error => {
      toast.error(error.message, { autoClose: 7500, theme: 'colored' });
      setIsModalOpen(false);
    }
  });

  const massTickerSearch = data?.massTickerSearch;
  const foundEntities = massTickerSearch?.foundEntities;
  const unfoundSymbols = massTickerSearch?.unfoundSymbols;

  const onDropFinished = (files: File[]): void => {
    const tickerCsvFile = files[0];

    if (tickerCsvFile) {
      setFileName(tickerCsvFile.name);

      const reader = new FileReader();
      reader.onload = (e): void => {
        const tickers = e.target?.result as string;
        if (tickers) {
          const splitTickers = tickers.split(/\r?\n/g).filter(value => value);

          lookupTickers({ variables: { tickers: splitTickers } });
        }
      };
      reader.readAsText(tickerCsvFile);
    }
  };

  const handleFollow = (): void => {
    if (!foundEntities) return;
    const entityIds = foundEntities.map(entity => entity.id);

    massFollowEntities({
      variables: { entityIds, fileName: fileName }
    });
  };

  const renderOpenModalButton = useCallback(
    () => (
      <Button
        disabled={lookupTickersLoading}
        onClick={() => setIsModalOpen(true)}
        text="Upload CSV"
      />
    ),
    [lookupTickersLoading]
  );

  return (
    <div className={styles.tickerUploader}>
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        renderOpenModalButton={renderOpenModalButton}
        title="Upload CSV"
      >
        <div className={styles.uploaderInfo}>
          Upload a CSV of Bloomberg-format tickers (e.g. FB:US, TSCO:LN, 083790:KS) to instantly
          follow and track them on SumZero.
        </div>
        <DropzoneInput accept={accept} onDrop={onDropFinished} filename={fileName} />
        {!massTickerSearch ? (
          <TickerPreUpload />
        ) : (
          <TickerPostUpload
            foundEntities={foundEntities || []}
            page={page}
            pageSize={40}
            setPage={(page: number): void => {
              setPage(page);
            }}
            unfoundSymbols={unfoundSymbols || []}
          />
        )}
        <div className={styles.buttonArea}>
          <Button
            isLoading={massFollowEntitiesLoading || lookupTickersLoading}
            analyticsTag="upload-csv-cancel"
            onClick={() => setIsModalOpen(false)}
            text="Cancel"
            type={BUTTON_TYPES.DANGER}
            size={BUTTON_SIZES.SMALL}
          />
          <Button
            analyticsTag="upload-csv-add-these-tickers"
            isLoading={Boolean(massFollowEntitiesLoading || lookupTickersLoading)}
            disabled={!massTickerSearch}
            id="add-tickers-button"
            onClick={(): void => handleFollow()}
            text="Add These Tickers"
            type={BUTTON_TYPES.PRIMARY}
            size={BUTTON_SIZES.SMALL}
          />
        </div>
      </Modal>
    </div>
  );
};

export default TickerUploader;
